import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/client";

function formatNum(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("he-IL");
}

function formatPct(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return `${Number(n).toFixed(2)}%`;
}

function sign(n) {
  if (n == null) return null;
  return n >= 0 ? "positive" : "negative";
}

// ── Stat Row ──────────────────────────────────────────────────────────────────

function StatRow({ label, value, highlight, indent }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "9px 0",
      borderBottom: "1px solid var(--border-light)",
      paddingRight: indent ? 16 : 0,
    }}>
      <span style={{ fontSize: 14, color: indent ? "var(--text-secondary)" : "var(--text)", fontWeight: indent ? 400 : 500 }}>
        {label}
      </span>
      <span style={{
        fontSize: 14, fontWeight: highlight ? 700 : 500,
        color: highlight === "positive" ? "#16a34a"
          : highlight === "negative" ? "#dc2626"
          : "var(--text)",
      }}>
        {value}
      </span>
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "income",   label: "דוח רווח והפסד", route: "/income-statement" },
  { id: "cashflow", label: "תזרים מזומנים",   route: "/cashflow"         },
  { id: "balance",  label: "מאזן",            route: "/balance-sheet"    },
];

// ── Tab Content ───────────────────────────────────────────────────────────────

function IncomeTab({ data }) {
  const inc = data.incomeStatement;
  return (
    <div>
      <StatRow label="סה״כ הכנסות"   value={`₪${formatNum(inc.totalRevenue)}`}  highlight="positive" />
      <StatRow label="סה״כ הוצאות"   value={`₪${formatNum(inc.totalExpenses)}`} highlight="negative" />
      <StatRow label="רווח נקי"      value={`₪${formatNum(inc.netIncome)}`}     highlight={sign(inc.netIncome)} />
      <StatRow label="מרג׳ין נקי"    value={formatPct(inc.marginPct)} />
    </div>
  );
}

function CashFlowTab({ data }) {
  const cf = data.cashFlow;
  return (
    <div>
      <StatRow label="תזרים תפעולי"  value={`₪${formatNum(cf.operatingNet)}`}  highlight={sign(cf.operatingNet)} />
      <StatRow label="תזרים השקעות"  value={`₪${formatNum(cf.investingNet)}`}  highlight={sign(cf.investingNet)} />
      <StatRow label="תזרים מימון"   value={`₪${formatNum(cf.financingNet)}`}  highlight={sign(cf.financingNet)} />
      <StatRow label="שינוי נטו"     value={`₪${formatNum(cf.netChange)}`}     highlight={sign(cf.netChange)} />
    </div>
  );
}

function BalanceTab({ data }) {
  const bs = data.balanceSheet;
  return (
    <div>
      <StatRow label="סה״כ נכסים"         value={`₪${formatNum(bs.totalAssets)}`}      highlight="positive" />
      <StatRow label="סה״כ התחייבויות"    value={`₪${formatNum(bs.totalLiabilities)}`} highlight="negative" />
      <StatRow label="הון עצמי"           value={`₪${formatNum(bs.netEquity)}`}        highlight={sign(bs.netEquity)} />
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function FinancialStatementsTabs() {
  const [activeTab, setActiveTab] = useState("income");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    apiClient.get(`/financial-summary`)
      .then((r) => setSummary(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const currentTab = TABS.find((t) => t.id === activeTab);

  return (
    <div className="page" style={{ maxWidth: 680 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.02em" }}>
          דוחות פיננסיים
        </h1>
        {summary?.period && (
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--text-secondary)" }}>
            תקופה: {summary.period.start} עד {summary.period.end}
          </p>
        )}
      </div>

      {/* Tab bar */}
      <div style={{
        display: "flex", gap: 4, marginBottom: 20,
        borderBottom: "1px solid var(--border-light)", paddingBottom: 0,
        direction: "rtl",
      }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: "9px 18px",
              fontSize: 14, fontWeight: activeTab === t.id ? 600 : 400,
              color: activeTab === t.id ? "var(--accent)" : "var(--text-secondary)",
              background: "none", border: "none", cursor: "pointer",
              borderBottom: activeTab === t.id ? "2px solid var(--accent)" : "2px solid transparent",
              marginBottom: -1,
              transition: "color 0.15s, border-color 0.15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="card" style={{ padding: "4px 20px" }}>
        {loading && (
          <div style={{ padding: "32px 0", textAlign: "center", color: "var(--text-secondary)", fontSize: 14 }}>
            טוען נתונים...
          </div>
        )}
        {error && (
          <div style={{ padding: "32px 0", textAlign: "center", color: "#dc2626", fontSize: 14 }}>
            שגיאה: {error}
          </div>
        )}
        {summary && !loading && (
          <>
            {activeTab === "income"   && <IncomeTab   data={summary} />}
            {activeTab === "cashflow" && <CashFlowTab data={summary} />}
            {activeTab === "balance"  && <BalanceTab  data={summary} />}
          </>
        )}
      </div>

      {/* Link to full page */}
      {currentTab && (
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <button
            onClick={() => navigate(currentTab.route)}
            style={{
              padding: "9px 24px", fontSize: 14, fontWeight: 500,
              background: "var(--accent)", color: "#fff",
              border: "none", borderRadius: "var(--radius-sm)",
              cursor: "pointer", transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
          >
            צפה בדוח המלא ←
          </button>
        </div>
      )}
    </div>
  );
}
